import React from 'react';
import Joyride, { Step, TooltipRenderProps } from 'react-joyride';


interface OnboardingTourProps {
  run: boolean;
  steps: Step[];
  onComplete: () => void;
}

  const OnboardingTour: React.FC<OnboardingTourProps> = ({ run, steps, onComplete }) => {
  const [isDark, setIsDark] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkTheme = () => {
      const htmlElement = document.documentElement;
      setIsDark(htmlElement.classList.contains('dark'));
    };
    
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkTheme();
    checkMobile();
    
    // Check theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    // Check window resize
    window.addEventListener('resize', checkMobile);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Skip tours entirely on mobile devices
  React.useEffect(() => {
    if (isMobile && run) {
      // Immediately complete the tour on mobile
      onComplete();
    }
  }, [isMobile, run, onComplete]);

  // Simple tour active state (desktop only)
  React.useEffect(() => {
    if (run && !isMobile) {
      document.body.classList.add('tour-active');
    } else {
      document.body.classList.remove('tour-active');
    }

    return () => {
      document.body.classList.remove('tour-active');
    };
  }, [run, isMobile]);

  // Custom tooltip component that renders HTML (desktop only)
  const CustomTooltip: React.FC<TooltipRenderProps> = ({
    continuous,
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    skipProps,
    tooltipProps,
    size,
    isLastStep,
  }) => {
    return (
      <div
        {...tooltipProps}
        style={{
          backgroundColor: isDark ? '#1e293b' : '#ffffff',
          borderRadius: '16px',
          boxShadow: isDark 
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(148, 163, 184, 0.1)' 
            : '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(148, 163, 184, 0.1)',
          border: 'none',
          maxWidth: '400px',
          width: 'auto',
          color: isDark ? '#f1f5f9' : '#1e293b',
          textAlign: 'center',
        }}
      >
        {/* Progress indicator */}
        <div style={{ 
          padding: '12px 16px 8px', 
          borderBottom: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ 
            fontSize: '12px', 
            color: isDark ? '#94a3b8' : '#64748b',
            fontWeight: '500'
          }}>
            Step {index + 1} of {size}
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {Array.from({ length: size }, (_, i) => (
              <div
                key={i}
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: i <= index ? '#6366f1' : isDark ? '#475569' : '#cbd5e1',
                  transition: 'all 0.2s ease'
                }}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div 
          dangerouslySetInnerHTML={{ __html: step.content as string }}
          style={{ 
            fontSize: '16px',
            lineHeight: '1.6',
            padding: '16px'
          }}
        />

        {/* Footer with buttons */}
        <div style={{ 
          padding: '16px', 
          borderTop: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            {...skipProps}
            style={{
              color: isDark ? '#94a3b8' : '#64748b',
              fontSize: '14px',
              fontWeight: '500',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px 12px'
            }}
          >
            Skip Tour
          </button>

          <div style={{ 
            display: 'flex', 
            gap: '8px'
          }}>
            {index > 0 && (
              <button
                {...backProps}
                style={{
                  backgroundColor: 'transparent',
                  color: isDark ? '#94a3b8' : '#64748b',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
                  cursor: 'pointer'
                }}
              >
                Back
              </button>
            )}
            
            <button
              {...primaryProps}
              style={{
                background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)',
                backgroundSize: '200% 200%',
                backgroundPosition: 'left center',
                color: '#ffffff',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 15px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundPosition = 'right center';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(48, 179, 221, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.2) inset';
                e.currentTarget.style.transform = 'translateY(-1px) scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundPosition = 'left center';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(48, 179, 221, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1) inset';
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
              }}
            >
              {isLastStep ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Don't render tour on mobile
  if (isMobile) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous={true}
      showProgress={false}
      showSkipButton={false}
      scrollToFirstStep={false}
      disableOverlayClose={false}
      spotlightClicks={false}
      disableScrolling={true}
      tooltipComponent={CustomTooltip}
      callback={({ status, action, index, type }) => {
        if (status === 'finished' || status === 'skipped') {
          onComplete();
        }
      }}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: '#6366f1',
        },
        beacon: {
          outer: '#6366f1',
          inner: '#8b5cf6',
        },
        spotlight: {
          backgroundColor: 'rgba(0, 0, 0, 0)',
          borderRadius: '8px',
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9998,
        },
      }}
      floaterProps={{
        disableAnimation: true,
        offset: 12,
        styles: {
          floater: {
            filter: 'drop-shadow(0 10px 25px rgba(0, 0, 0, 0.1))',
          }
        }
      }}
    />
  );
};

export default OnboardingTour;
